import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GoodsCard, List, type ListRef, useScreenSize } from '@banhala/ui-kit';
import { useAtomValue } from 'jotai';
import { isAndroid, isNotNil, useNavigator } from 'shared/src/utils';
import { RankingGoodsListHeader } from './components/RankingGoodsListHeader';
import { useRankingGoods } from './queries/useRankingGoods';
import { rankingFilterAtom } from './states/rankingFilter.atom';
import { HomeScrollContext } from '../../context/HomeScrollContext';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import {
  convertRankingGoodsItemToGoodsData,
  type RankingGoodsCardItem,
} from './utils/convertRankingGoodsItemToGoodsData';
import { useAddLikeGoods, useLikeMap } from 'shared/src/hooks';
import { useSyncLocalGoodsLike } from 'shared/src/hooks/useSyncLocalGoodsLike';
import { useRemoveLikeGoods } from 'shared/src/queries';
import { bridge } from 'shared/src/utils';
import { useLogViewGoodsRanking } from './hooks/useLogViewGoodsRanking';
import { useLogDisplayGoods } from './hooks/useLogDisplayGoods';
import { useLogClickGoods } from './hooks/useLogClickGoods';
import { useLogClickLikeGoods } from './hooks/useLogClickLikeGoods';
import { GOODS_RANKING_SCREEN } from './hooks/getCommonFilterAnalytics';
import { useRegisterScrollToTop } from '../../hooks/useRegisterScrollToTop';

const VIEWABILITY_CONFIG = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};
const NUM_COLUMNS = 2;
const COLUMN_GAP = 2;

export type GoodsRankingHandle = { scrollToTop: () => void };

type GoodsRankingProps = {
  /**
   * 외부에서 스크롤 위치를 구독. screen 단독 호스팅 시 floating button 펼침/접힘
   * 트리거에 사용. HomeScrollContext 와 병행 호출되며 둘 다 안전하게 noop 가능.
   */
  onScrollY?: (y: number) => void;
};

const GoodsRankingImpl = (
  { onScrollY: onScrollYProp }: GoodsRankingProps,
  ref: React.Ref<GoodsRankingHandle>,
) => {
  const filter = useAtomValue(rankingFilterAtom);
  const { navigator } = useNavigator();

  const selectedCategorySno =
    filter.selectedSubCategory?.query_value.category_sno ??
    filter.selectedCategory?.query_value.category_sno ??
    null;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, fetchNextPage, hasNextPage, refetch, isSuccess } =
    useRankingGoods({
      filter: 'best',
      market_type_sno:
        filter.selectedMarketType?.query_value.market_type_sno ?? undefined,
      category_sno: selectedCategorySno ?? undefined,
      'age_tags[]': filter.selectedAge?.query_value['age_tags[]'] ?? undefined,
      period: filter.selectedPeriod?.value ?? undefined,
    });

  const list = useMemo(() => {
    if (!data?.goods) return [];
    return data.goods.map(convertRankingGoodsItemToGoodsData);
  }, [data?.goods]);

  const { likeMap, setLikeMap } = useLikeMap({
    initialLikeMap: {},
  });
  useEffect(() => {
    if (isSuccess && isNotNil(data)) {
      setLikeMap(prev => {
        return {
          ...prev,
          ...data.goods.reduce((cur, acc) => {
            return {
              ...cur,
              [acc.sno]: isNotNil(acc.like),
            };
          }, {}),
        };
      });
    }
  }, [data, isSuccess, setLikeMap]);

  const { addLikeGoods } = useAddLikeGoods({
    listId: data?.list_id ?? '',
    listParams: undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prev => ({ ...prev, [sno]: nextValue }));
    },
    screen: GOODS_RANKING_SCREEN,
    offsetY: 52,
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();

  useSyncLocalGoodsLike(setLikeMap);

  useLogViewGoodsRanking();
  const logDisplayGoods = useLogDisplayGoods({
    listId: data?.list_id,
    listParams: data?.list_params,
  });
  const logClickGoods = useLogClickGoods({
    listId: data?.list_id,
    listParams: data?.list_params,
  });
  const logClickLikeGoods = useLogClickLikeGoods({
    listId: data?.list_id,
    listParams: data?.list_params,
  });

  const { width: screenWidth } = useScreenSize();

  const itemWidth = useMemo(
    () => (screenWidth - COLUMN_GAP * (NUM_COLUMNS - 1)) / NUM_COLUMNS,
    [screenWidth],
  );

  const homeScroll = useContext(HomeScrollContext);

  const listRef = useRef<ListRef<RankingGoodsCardItem>>(null);
  const handleScrollToTop = useCallback(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, []);
  useRegisterScrollToTop(GOODS_RANKING_SCREEN, handleScrollToTop);
  useImperativeHandle(ref, () => ({ scrollToTop: handleScrollToTop }), [
    handleScrollToTop,
  ]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const y = e.nativeEvent.contentOffset.y;
      homeScroll?.onScrollY(y);
      onScrollYProp?.(y);
    },
    [homeScroll, onScrollYProp],
  );

  const handlePressItem = (pressed: RankingGoodsCardItem, index: number) => {
    const { item } = pressed;
    logClickGoods(pressed, index);
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering
        ? {
            discount_rate: item.first_page_rendering.discount_rate,
            goods_name: item.first_page_rendering.goods_name,
            original_price: item.first_page_rendering.original_price,
            price: item.first_page_rendering.price,
            cover_image: item.first_page_rendering.cover_image,
            market_image: item.first_page_rendering.market_image ?? undefined,
            market_name: item.first_page_rendering.market_name ?? undefined,
          }
        : undefined,
    });
  };

  const handlePressLike = async (
    pressed: RankingGoodsCardItem,
    index: number,
  ) => {
    const { item } = pressed;
    const isLike = !likeMap[item.sno];
    logClickLikeGoods(pressed, index, isLike);

    if (isLike) {
      addLikeGoods(item.sno, { SCREEN: 'RANKING' });
      return;
    }

    await mutateRemoveLikeGoods({ sno: item.sno });
    bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
    setLikeMap(prev => ({ ...prev, [item.sno]: false }));
  };

  return (
    <List<RankingGoodsCardItem>
      ref={listRef}
      numColumns={2}
      data={list}
      keyExtractor={(item, index) => (item.sno ?? index).toString()}
      renderItem={({ item, index }) => (
        <GoodsCard
          data={item}
          isLike={likeMap[item.sno]}
          onPress={() => handlePressItem(item, index)}
          onPressLike={() => handlePressLike(item, index)}
          defaultWidth={itemWidth}
          rankingBackgroundColor={'black'}
          size={'medium'}
          testID={`goods/${item.sno}?LIST_ID=${item.logging.analytics?.LIST_ID}&LIST_INDEX=${index}&DELIVERY_TYPE=${item.deliveryType}`}
        />
      )}
      windowSize={isAndroid ? 2 : 10}
      initialNumToRender={4}
      refreshing={isRefreshing}
      onRefresh={() => {
        setIsRefreshing(true);
        refetch().finally(() => setIsRefreshing(false));
      }}
      ListHeaderComponent={
        <React.Suspense fallback={<></>}>
          <RankingGoodsListHeader
            lastUpdatedBeforeMessage={data?.last_updated_before_message}
          />
        </React.Suspense>
      }
      onEndReached={() => {
        if (hasNextPage) {
          fetchNextPage();
        }
      }}
      columnWrapperStyle={{ gap: COLUMN_GAP }}
      onEndReachedThreshold={1}
      onViewableItemsChanged={logDisplayGoods}
      viewabilityConfig={VIEWABILITY_CONFIG}
      onScroll={handleScroll}
    />
  );
};

export const GoodsRanking = forwardRef(GoodsRankingImpl);
